<template>
  <div class="w-full max-w-md text-center" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="w-16 h-16 rounded-2xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 flex items-center justify-center shadow-sm mx-auto mb-6">
      <Icon icon="eos-icons:loading" width="34" class="text-stone-600 dark:text-stone-300" />
    </div>
    <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
      {{ t('agentCreate.generating.title', 'Generating your agent') }}
    </h2>
    <p class="text-sm text-gray-600 dark:text-gray-400 leading-6 min-h-[3rem]" :dir="isRtl === true ? 'rtl' : 'ltr'" style="unicode-bidi: isolate; text-align: center;">
      {{ displayedStatus }}
    </p>
    <div class="mt-6 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
      <div class="h-full bg-stone-700 dark:bg-stone-300 transition-all duration-500 ease-out" :style="{ width: progress + '%' }"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';

const props = defineProps({
  createAgent: {
    type: Function,
    default: null
  }
});

const emit = defineEmits(['complete']);

const { t, currentLanguage, availableLanguages } = useI18n();

const isRtl = computed(() => {
  const language = availableLanguages.value.find((item) => item.code === currentLanguage.value);
  if (language === undefined || language === null) {
    return false;
  }
  return language.dir === 'rtl';
});

const statusMessages = computed(() => [
  t('agentCreate.generating.status.profile', 'Analyzing your agent profile...'),
  t('agentCreate.generating.status.knowledge', 'Building knowledge configuration...'),
  t('agentCreate.generating.status.channels', 'Connecting deployment channels...'),
  t('agentCreate.generating.status.personality', 'Applying personality and tone...'),
  t('agentCreate.generating.status.finalizing', 'Finalizing your agent...')
]);

const progress = ref(0);
const statusIndex = ref(0);
const displayedStatus = ref('');
const creationComplete = ref(false);
const creationFailed = ref(false);
let progressTimer = null;
let statusTimer = null;
let streamTimer = null;

function streamStatusText(text) {
  displayedStatus.value = '';
  let index = 0;
  if (streamTimer !== null) {
    window.clearInterval(streamTimer);
  }
  streamTimer = window.setInterval(() => {
    if (index < text.length) {
      displayedStatus.value = displayedStatus.value + text[index];
      index = index + 1;
    } else {
      window.clearInterval(streamTimer);
      streamTimer = null;
    }
  }, 42);
}

function advanceStatus() {
  const message = statusMessages.value[statusIndex.value];
  if (typeof message === 'string') {
    streamStatusText(message);
  }
  statusIndex.value = statusIndex.value + 1;
  if (statusIndex.value >= statusMessages.value.length) {
    window.clearInterval(statusTimer);
    statusTimer = null;
  }
}

async function createAgent() {
  if (typeof props.createAgent !== 'function') {
    creationComplete.value = true;
    return;
  }
  try {
    await props.createAgent();
    creationComplete.value = true;
  } catch (error) {
    creationFailed.value = true;
    const responseData = error !== undefined && error !== null && error.response !== undefined && error.response !== null ? error.response.data : null;
    const responseMessage = responseData !== null && responseData !== undefined && typeof responseData.message === 'string' ? responseData.message : (responseData !== null && responseData !== undefined && typeof responseData.error === 'string' ? responseData.error : '');
    const errorMessage = responseMessage.length > 0 ? responseMessage : (error !== undefined && error !== null && typeof error.message === 'string' ? error.message : '');
    emit('error', errorMessage);
  }
}

onMounted(() => {
  advanceStatus();
  statusTimer = window.setInterval(() => {
    advanceStatus();
  }, 2400);
  progressTimer = window.setInterval(() => {
    if (creationFailed.value === true) {
      window.clearInterval(progressTimer);
      progressTimer = null;
      return;
    }
    if (progress.value < 90 && creationComplete.value === false) {
      progress.value = progress.value + 2;
      return;
    }
    if (creationComplete.value === true && progress.value < 100) {
      progress.value = progress.value + 2;
      return;
    }
    if (creationComplete.value === true && progress.value >= 100) {
      window.clearInterval(progressTimer);
      progressTimer = null;
      window.setTimeout(() => {
        emit('complete');
      }, 800);
    }
  }, 180);
  createAgent();
});

onBeforeUnmount(() => {
  if (progressTimer !== null) {
    window.clearInterval(progressTimer);
  }
  if (statusTimer !== null) {
    window.clearInterval(statusTimer);
  }
  if (streamTimer !== null) {
    window.clearInterval(streamTimer);
  }
});
</script>

